<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="700" scrollable>
    <v-card min-height="300">
      <v-card-title>
        Replace default: {{ componentName }}
        <v-spacer/>
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-progress-linear indeterminate v-if="loading"/>
        <template v-if="!loading && usage">
          <v-alert type="info" outlined dense>
            {{ usage.meals_using_default }} of {{ usage.meal_count }} meals in the last
            {{ usage.months }} months use the current default;
            {{ usage.meal_count - usage.meals_using_default }} use a different version.
          </v-alert>
          <p v-if="usage.variations.length === 0" class="caption">
            No meals with weighed ingredients for this component since {{ usage.from_date }}.
          </p>
          <v-radio-group v-model="selectedIndex">
            <div
              v-for="(variation, index) of usage.variations" v-bind:key="index"
              class="variation mb-3"
            >
              <v-radio :value="index" :disabled="variation.is_default">
                <template v-slot:label>
                  <div>
                    <strong>{{ variation.meal_count }} meal{{ variation.meal_count === 1 ? '' : 's' }}</strong>
                    <v-chip v-if="variation.is_default" small outlined color="green" class="ml-2">
                      current default
                    </v-chip>
                  </div>
                </template>
              </v-radio>
              <div class="ml-8">
                <ul class="caption">
                  <li v-for="ingredient of variation.ingredients" v-bind:key="ingredient.name">
                    {{ ingredient.amount }}g {{ ingredient.name }}
                  </li>
                </ul>
                <div class="caption grey--text">
                  {{ formatMeals(variation) }}
                </div>
              </div>
            </div>
          </v-radio-group>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn text @click="$emit('input', false)">Cancel</v-btn>
        <v-btn
          color="primary"
          :disabled="selectedIndex === null"
          :loading="replacing"
          @click="replaceDefault"
        >
          Replace Default
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api';
import moment from 'moment';

export default {
  name: 'ReplaceDefaultDialog',
  props: {
    value: {type: Boolean, default: false},
    componentId: {type: Number, required: false},
    componentName: {type: String, default: ''},
  },
  data() {
    return {
      loading: null,
      usage: null,
      selectedIndex: null,
      replacing: null,
    };
  },
  watch: {
    value(open) {
      if (open) {
        this.selectedIndex = null;
        this.fetchVariations();
      }
    },
  },
  methods: {
    formatMeals(variation) {
      const names = variation.meals
        .map(meal => `${meal.name} (${moment(meal.date).format('MMM D')})`)
        .join(', ');
      const more = variation.meal_count > variation.meals.length
        ? ` +${variation.meal_count - variation.meals.length} more`
        : '';
      return names + more;
    },
    async fetchVariations() {
      this.loading = true;
      this.usage = null;
      try {
        const {data} = await api.get(`v2/component/${this.componentId}/variations`);
        this.usage = data;
      } catch (e) {
        console.error('failed to fetch component variations', e);
        alert(`failed to fetch component variations: ${e.message}`);
      } finally {
        this.loading = false;
      }
    },
    async replaceDefault() {
      const variation = this.usage.variations[this.selectedIndex];
      if (!variation) {
        return;
      }
      const message = `Replace the default ingredients of ${this.componentName} (C${this.componentId}) ` +
        `with the version used by ${variation.meal_count} meal(s)?\n\n` +
        `This changes the recipe used wherever the default is copied from.`;
      if (!confirm(message)) {
        return;
      }
      this.replacing = true;
      try {
        // the variations endpoint only has per-100g summaries, so fetch the full
        // meal_food rows (measure, portion multiplier, prep steps) from the
        // variation's most recent meal - the same flow ComponentDetail uses
        const {data} = await api.get(`v2/component/${this.componentId}/ingredients`);
        const ingredients = (data.meal_foods || []).filter(mf => mf.mealid === variation.source_meal_id);
        if (ingredients.length === 0) {
          alert('no weighed ingredients found for the selected meal version');
          return;
        }
        await api.put(`v2/component/${this.componentId}/ingredients`, {ingredients});
        this.$emit('replaced', {componentId: this.componentId});
        this.$emit('input', false);
      } catch (e) {
        console.error('failed to replace default component', e);
        alert(`failed to replace default: ${e.message}`);
      } finally {
        this.replacing = false;
      }
    },
  },
};
</script>

<style scoped>
.variation {
  border-left: 2px solid #ddd;
  padding-left: 8px;
}
</style>
